import axios from 'axios';

import config from '@/config';
import { APIClient } from '@/helpers/api_helper';

import { QuoteStatus, type QuoteStatusValue } from '../schemas';
import type {
  Quote,
  QuoteListFilters,
  QuoteListResponse,
  UpdateQuoteStatusPayload,
} from '../types';
import type { QuotePayload } from '../types/quote-form';
import type {
  QuoteConvertToWorkOrderPayload,
  QuoteConvertToWorkOrderResponse,
  QuoteEmailPayload,
  QuoteEmailTemplate,
  QuoteWithItems,
} from '../types/quote-detail';
import type { QuoteStatusHistoryEntry } from '../types/quote-history';

/**
 * Quotes API client — wraps the shared axios `APIClient`.
 *
 * Endpoints mirrored from legacy `src/services/quoteService.ts` and
 * `src/services/quoteStatusService.ts`:
 *
 *   GET    /Quote?pageNumber=X&pageSize=Y
 *   GET    /Quote/:id?includeItems=true&includeItemDetails=true
 *   POST   /Quote                           — create (Agent B)
 *   PUT    /Quote/:id                       — update (Agent B)
 *   DELETE /Quote/:id
 *   POST   /Quote/:id/status                — single status transition
 *
 * The status endpoint is the ONLY supported way to change a quote's workflow
 * state in the modern stack. Comment is required for Approve / RequestChanges
 * / Reject and ignored elsewhere.
 *
 * NOTE: Agent A owns this file. Agent B will append create/update functions
 * at the marked location at the bottom — please do not refactor existing
 * exports.
 */

const QUOTE_BASE_URL = '/Quote';

const api = new APIClient();

const emptyListResponse = (filters: QuoteListFilters): QuoteListResponse => ({
  items: [],
  pageNumber: filters.pageNumber ?? 1,
  pageSize: filters.pageSize ?? 10,
  totalCount: 0,
  totalPages: 0,
  hasPreviousPage: false,
  hasNextPage: false,
});

/**
 * Normalise the pageable envelope — backend may return a partial shape on 204.
 */
function normaliseList(
  response: Partial<QuoteListResponse> | undefined,
  filters: QuoteListFilters,
): QuoteListResponse {
  return {
    items: response?.items ?? [],
    pageNumber: response?.pageNumber ?? filters.pageNumber ?? 1,
    pageSize: response?.pageSize ?? filters.pageSize ?? 10,
    totalCount: response?.totalCount ?? 0,
    totalPages: response?.totalPages ?? 0,
    hasPreviousPage: response?.hasPreviousPage ?? false,
    hasNextPage: response?.hasNextPage ?? false,
  };
}

export const quotesApi = {
  list: async (
    filters: QuoteListFilters = {},
  ): Promise<QuoteListResponse> => {
    const { pageNumber = 1, pageSize = 200 } = filters;
    try {
      const response = await api.get<QuoteListResponse>(QUOTE_BASE_URL, {
        pageNumber,
        pageSize,
      });
      return normaliseList(response, filters);
    } catch (error) {
      if (typeof error === 'string') {
        throw new Error(error);
      }
      if (error instanceof Error) {
        throw error;
      }
      return emptyListResponse(filters);
    }
  },

  getById: (id: string, includeItems = false): Promise<Quote> => {
    return api.get<Quote>(`${QUOTE_BASE_URL}/${id}`, {
      includeItems,
      includeItemDetails: includeItems,
    });
  },

  remove: (id: string): Promise<void> => {
    return api.delete<void>(`${QUOTE_BASE_URL}/${id}`);
  },

  /**
   * The single status-driven workflow endpoint. Pass a comment for
   * Approve / RequestChanges / Reject.
   */
  updateStatus: (
    id: string,
    payload: UpdateQuoteStatusPayload,
  ): Promise<Quote> => {
    const body: UpdateQuoteStatusPayload = {
      status: payload.status,
      ...(payload.comment ? { comment: payload.comment } : {}),
    };
    return api.create<Quote>(`${QUOTE_BASE_URL}/${id}/status`, body);
  },
};

/**
 * Convenience helpers — thin wrappers around `updateStatus` for the most
 * common status transitions consumed by `mutations.ts`. Keeping the names
 * tight makes mutation hooks read cleanly at the call site.
 */
export const quotesStatusActions = {
  approve: (id: string, comment: string) =>
    quotesApi.updateStatus(id, { status: QuoteStatus.Approved, comment }),
  reject: (id: string, comment: string) =>
    quotesApi.updateStatus(id, { status: QuoteStatus.Rejected, comment }),
  requestChanges: (id: string, comment: string) =>
    quotesApi.updateStatus(id, {
      status: QuoteStatus.ChangeRequested,
      comment,
    }),
  submitForReview: (id: string) =>
    quotesApi.updateStatus(id, { status: QuoteStatus.UnderReview }),
  send: (id: string) =>
    quotesApi.updateStatus(id, { status: QuoteStatus.Sent }),
  award: (id: string) =>
    quotesApi.updateStatus(id, { status: QuoteStatus.Awarded }),
  expire: (id: string) =>
    quotesApi.updateStatus(id, { status: QuoteStatus.Expired }),
  /**
   * Generic transition — used by the Kanban board where the target status
   * is the column the card was dropped onto.
   */
  setStatus: (id: string, status: QuoteStatusValue, comment?: string) =>
    quotesApi.updateStatus(id, { status, comment }),
};

// Agent B: append createQuote / updateQuote functions below this line.

/**
 * Quote builder API — create / update / fetch-with-items.
 *
 * Agent B owns these endpoints. Mirrors the legacy contract in
 * `src/services/quoteService.ts`:
 *
 *   POST   /Quote                                — create (returns Quote)
 *   PUT    /Quote/:id                            — update (returns Quote)
 *   GET    /Quote/:id?includeItems=true&...      — fetch with line items
 *   POST   /Quote/AutogenerateQuotationCode      — auto code generator
 */
export const quotesBuilderApi = {
  /**
   * `GET /Quote/:id` with nested items.
   *
   * Returns the heavy detail shape (`QuoteWithItems` from Agent C's
   * `quote-detail.ts`) — used by the Edit page to hydrate the form. `Quote`
   * (the lean list row) is a structural subset.
   */
  getDetail: (id: string): Promise<QuoteWithItems> => {
    return api.get<QuoteWithItems>(`${QUOTE_BASE_URL}/${id}`, {
      includeItems: true,
      includeItemDetails: true,
    });
  },

  /**
   * `POST /Quote` — create a new quote.
   *
   * Returns the freshly created quote. The backend assigns Draft status, the
   * `id`, `quoteNumber` (unless provided), and tenant scoping.
   */
  create: (payload: QuotePayload): Promise<Quote> => {
    return api.create<Quote>(QUOTE_BASE_URL, payload);
  },

  /**
   * `PUT /Quote/:id` — update an existing quote (header + jobs + items).
   *
   * The payload mirrors `create` — the backend treats the operation as a full
   * replace of the editable fields (same as the legacy implementation in
   * `quoteService.updateQuote`).
   */
  update: (id: string, payload: QuotePayload): Promise<Quote> => {
    // Deep-clone to strip any React/Redux references — matches legacy behaviour.
    const serialized = JSON.parse(JSON.stringify(payload));
    return api.put<Quote>(`${QUOTE_BASE_URL}/${id}`, serialized);
  },

  /**
   * `POST /Quote/AutogenerateQuotationCode` — fetches the next display code for
   * a new Quote. Used by the form's "Auto" toggle.
   */
  autogenerateCode: (): Promise<{
    isSuccess: boolean;
    message: string;
    generatedCode: string;
  }> => {
    return api.create<{
      isSuccess: boolean;
      message: string;
      generatedCode: string;
    }>(`${QUOTE_BASE_URL}/AutogenerateQuotationCode`, {});
  },
};

/* -------------------------------------------------------------------------- */
/* Agent C — Quote View / Preview / Email / Convert                           */
/* -------------------------------------------------------------------------- */

/**
 * Dedicated axios instance for PDF blob downloads.
 *
 * Bypasses the shared response interceptor so binary data isn't corrupted by
 * the `response.data` extractor. Mirrors the legacy `blobAxios` pattern from
 * `src/services/quoteService.ts`.
 */
const blobAxios = axios.create({
  baseURL: config.api.API_URL,
  responseType: 'blob',
});

blobAxios.interceptors.request.use((req) => {
  // Use the SAME token-resolution path as the legacy blobAxios — the
  // canonical `authService.getToken()` reads `sessionStorage.persist:root`
  // → parsed.Login → loginState.user.token. A previous version of this
  // interceptor tried `sessionStorage.getItem('authUser')` (wrong key),
  // which silently sent the request unauthenticated and the backend
  // responded 404. Lazy-imported to avoid the top-level circular dep
  // with `services/authService.ts`.
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { authService } = require('@/services/authService');
    const token = authService?.getToken?.();
    if (token) {
      req.headers = req.headers || {};
      req.headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    /* fall through — request fires without auth and the server returns 401 */
  }
  return req;
});

/**
 * View / Preview / Email / Convert API surface.
 *
 * Endpoints (per Wave 5 brief):
 *   GET    /Quote/:id?includeItems=true        — detail with nested jobs/items
 *   GET    /Quote/:id/EmailTemplate            — optional default subject/body
 *   POST   /Quote/:id/SendEmail                — trigger email dispatch
 *   POST   /Quote/:id/AwardToWorkOrder         — Award + WO generation
 *   GET    /Quote/:id/GeneratePdf              — printable PDF blob
 *
 * NOTE: `SendEmail` and `EmailTemplate` are not yet exposed by the backend
 * (Wave 5 brief). The mutation surface is present so the dialog has a clean
 * call site — when the backend ships, only these implementations change.
 */
export const quotesViewApi = {
  /** Detail-with-items query for the View / Preview / Convert flows. */
  getDetail: (id: string): Promise<QuoteWithItems> => {
    return api.get<QuoteWithItems>(`${QUOTE_BASE_URL}/${id}`, {
      includeItems: true,
      includeItemDetails: true,
    });
  },

  /** Status-transition history for the lifecycle timeline (existing endpoint). */
  getStatusHistory: async (id: string): Promise<QuoteStatusHistoryEntry[]> => {
    const res = await api.get<QuoteStatusHistoryEntry[]>(
      `${QUOTE_BASE_URL}/${id}/StatusHistory`,
    );
    return Array.isArray(res) ? res : [];
  },

  /**
   * Fetch the default email template (subject + body) for a given quote.
   * Returns `null` when the backend hasn't shipped the endpoint yet so the
   * dialog can fall back to a hard-coded default.
   */
  getEmailTemplate: async (
    id: string,
  ): Promise<QuoteEmailTemplate | null> => {
    try {
      const response = await api.get<QuoteEmailTemplate>(
        `${QUOTE_BASE_URL}/${id}/EmailTemplate`,
      );
      return response ?? null;
    } catch (error) {
      // 404 (endpoint not yet implemented) is a routine state — return null so
      // the dialog falls back to its built-in default. Real errors propagate.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 405 || status === 501) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Trigger email dispatch for the quote. Backend handles PDF generation when
   * `attachPreviewAsPdf` is true.
   */
  sendEmail: (id: string, payload: QuoteEmailPayload): Promise<void> => {
    return api.create<void>(`${QUOTE_BASE_URL}/${id}/SendEmail`, payload);
  },

  /**
   * Convert an Awarded quote to a Work Order.
   *
   * The legacy backend endpoint is `POST /Quote/:id/AwardToWorkOrder`; the
   * brief calls this "Convert to Work Order" in the modern UI. We hit the
   * legacy URL to match the deployed backend until Wave 6 ships the rename.
   * The body uses the legacy PascalCase field names (matches
   * `AwardQuoteRequest`).
   */
  convertToWorkOrder: (
    id: string,
    siteId: string,
    payload: QuoteConvertToWorkOrderPayload,
  ): Promise<QuoteConvertToWorkOrderResponse> => {
    return api.create<QuoteConvertToWorkOrderResponse>(
      `${QUOTE_BASE_URL}/${id}/AwardToWorkOrder`,
      {
        SiteId: siteId,
        WorkOrderTitle: payload.workOrderTitle,
        StartDate: payload.startDate,
        CreateSeparateWorkOrdersPerQuoteJob:
          payload.createSeparateWorkOrdersPerQuoteJob,
      },
    );
  },

  /**
   * Stream a printable PDF for the quote. Returns a `Blob` so the caller can
   * trigger a download via `URL.createObjectURL`.
   */
  generatePdf: async (id: string): Promise<Blob> => {
    const response = await blobAxios.get(
      `${QUOTE_BASE_URL}/${id}/GeneratePdf`,
      {
        headers: { Accept: 'application/pdf' },
      },
    );
    if (response.data instanceof Blob) {
      return response.data;
    }
    return new Blob([response.data], { type: 'application/pdf' });
  },
};
